// =============================================================================
// ChatbotScreen: de onderwerpen (intents) en antwoorden van de klantenservicebot
// =============================================================================
// De bot herkent per klantvraag een onderwerp (Jev kiest op de criteria) en
// geeft daar het bijbehorende antwoord op, of verwijst door naar een collega.
// Hier beheert staf die catalogus. Elke wijziging is eerst een concept; pas
// "Goedkeuren en live zetten" verandert wat de bot zegt. Onderwerpen "met
// logica" hebben hun antwoordtekst in de code: daar zijn alleen de criteria,
// titels en de schakelaars aanpasbaar, de tekst is te zien in het voorbeeld.
//
// Links de lijst, rechts de editor met eronder het voorbeeld en de testzin.
// Het tabblad "Onbeantwoord" toont wat de bot niet kon plaatsen of doorgaf,
// met per regel de keuze: nieuw onderwerp, of als voorbeeldzin bij een
// bestaand onderwerp. Die regels zijn data, nooit instructies.
//
// Na elke opslag en goedkeuring draait check_examples: elk trefwoord in de
// criteria verschuift ook de buren, en de "flips" laten zien welke
// voorbeeldzinnen daardoor nu ergens anders landen.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  approveIntent,
  checkExamples,
  classifyText,
  createIntent,
  discardIntentDraft,
  emptyIntentFields,
  INTENT_KEY_RE,
  listIntents,
  dismissUnanswered,
  listUnanswered,
  previewIntent,
  saveIntentDraft,
  type ClassifyResult,
  type ExampleCheckResult,
  type IntentActionKind,
  type IntentFields,
  type IntentLocale,
  type IntentPreview,
  type IntentRow,
  type PreviewVenue,
  type SupportAdminError,
  type UnansweredResponse,
} from '../api/supportAdmin';

type ChatbotTab = 'intents' | 'unanswered';

const ERROR_LABEL: Record<string, string> = {
  permission_denied: 'Je hebt geen rechten om de chatbot te beheren.',
  unauthenticated: 'Je bent niet ingelogd.',
  invalid_key: 'De sleutel moet beginnen met een letter en mag alleen kleine letters, cijfers en _ bevatten (3 tot 41 tekens).',
  key_exists: 'Er bestaat al een onderwerp met deze sleutel.',
  not_found: 'Dit onderwerp bestaat niet (meer).',
  no_draft: 'Er is geen concept om goed te keuren.',
  invalid_draft: 'Het concept is niet compleet.',
  network_error: 'Geen verbinding met de server.',
};

const ACTION_LABEL: Record<IntentActionKind, string> = {
  book: 'Boekknop',
  talk: 'Naar een collega',
  identify: 'Boeking opzoeken',
};
const ACTION_KINDS: IntentActionKind[] = ['book', 'talk', 'identify'];

const VENUE_LABEL: Record<PreviewVenue, string> = {
  eindhoven: 'Eindhoven',
  amsterdam: 'Amsterdam',
  unknown: 'Onbekend',
};
const VENUES: PreviewVenue[] = ['eindhoven', 'amsterdam', 'unknown'];

function errText(e: SupportAdminError | null): string {
  if (!e) return 'Onbekende fout';
  const base = ERROR_LABEL[e.error];
  if (base) return e.message ? `${base} (${e.message})` : base;
  return e.message ? `${e.error}: ${e.message}` : e.error;
}

function pct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '–';
  return `${Math.round(n * 100)}%`;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Drop empty example lines before anything goes to the server; the textarea
// keeps them so typing a newline behaves.
function cleanFields(f: IntentFields): IntentFields {
  return {
    ...f,
    criteria: f.criteria.trim(),
    title_nl: f.title_nl.trim(),
    title_en: f.title_en.trim(),
    examples: f.examples.map((s) => s.trim()).filter((s) => s.length > 0),
    links: f.links.filter((l) => l.url.trim().length > 0 || l.label_nl.trim().length > 0 || l.label_en.trim().length > 0),
    notes: f.notes && f.notes.trim().length > 0 ? f.notes : null,
  };
}

function fieldsEqual(a: IntentFields, b: IntentFields): boolean {
  return JSON.stringify(cleanFields(a)) === JSON.stringify(cleanFields(b));
}

// Client-side pre-check for friendly messages before a round-trip. The server
// remains authoritative.
function fieldsProblem(f: IntentFields, kind: 'plain' | 'code'): string | null {
  if (!f.criteria.trim()) return 'Vul de criteria in: waar gaat dit onderwerp over?';
  if (!f.title_nl.trim() || !f.title_en.trim()) return 'Vul de titel in het Nederlands en het Engels in.';
  if (kind === 'plain' && (!(f.text_nl ?? '').trim() || !(f.text_en ?? '').trim())) {
    return 'Vul het antwoord in het Nederlands en het Engels in.';
  }
  return null;
}

// ---------------------------------------------------------------------------
export function ChatbotScreen() {
  const [intents, setIntents] = useState<IntentRow[]>([]);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<ChatbotTab>('intents');
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Non-null = the create form is open, seeded with the given fields (an
  // unanswered sentence arrives here as the first example).
  const [creating, setCreating] = useState<IntentFields | null>(null);
  const [check, setCheck] = useState<ExampleCheckResult | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await listIntents();
    if (res.ok && res.data) {
      setIntents(res.data.intents);
      setPlaceholders(res.data.placeholders);
    } else {
      setError(errText(res.error));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceIntent = useCallback((row: IntentRow) => {
    setIntents((rows) => {
      const i = rows.findIndex((r) => r.key === row.key);
      if (i < 0) return [...rows, row];
      const next = rows.slice();
      next[i] = row;
      return next;
    });
  }, []);

  // Every save and approve re-runs the example check; that is the only
  // feedback staff get that a criteria edit moved a neighbouring intent.
  const runCheck = useCallback(async (useDrafts: boolean) => {
    const res = await checkExamples(useDrafts ? true : undefined);
    if (res.ok && res.data) setCheck(res.data);
    else setError(errText(res.error));
  }, []);

  const selected = useMemo(
    () => intents.find((r) => r.key === selectedKey) ?? null,
    [intents, selectedKey],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return intents;
    return intents.filter((r) => {
      const f = r.draft ?? r.live;
      return (
        r.key.includes(q) ||
        f.title_nl.toLowerCase().includes(q) ||
        f.title_en.toLowerCase().includes(q) ||
        f.criteria.toLowerCase().includes(q)
      );
    });
  }, [intents, search]);

  const openCreate = (seed?: IntentFields) => {
    setCreating(seed ?? emptyIntentFields());
    setSelectedKey(null);
    setTab('intents');
  };

  return (
    <div className="screen chatbot-screen">
      <div className="screen-header">
        <h1>Chatbot</h1>
      </div>
      <div className="marketing-lang-tabs">
        <button
          className={tab === 'intents' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
          onClick={() => setTab('intents')}
        >
          Onderwerpen
        </button>
        <button
          className={tab === 'unanswered' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
          onClick={() => setTab('unanswered')}
        >
          Onbeantwoord
        </button>
      </div>

      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <p className="muted">Laden…</p> : null}

      {tab === 'unanswered' ? (
        <UnansweredTab
          intents={intents}
          onNewIntent={(text) => openCreate({ ...emptyIntentFields(), examples: [text] })}
          onAssigned={(row) => {
            replaceIntent(row);
            void runCheck(true);
          }}
          onError={setError}
        />
      ) : null}

      {tab === 'intents' && !loading ? (
        <div className="chatbot-layout">
          <aside className="chatbot-list">
            <div className="chatbot-list-tools">
              <input
                type="search"
                placeholder="Zoek onderwerp…"
                aria-label="Zoek onderwerp"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button className="walkin-primary-btn" onClick={() => openCreate()}>
                Nieuw intent
              </button>
            </div>
            {filtered.length === 0 ? <p className="muted">Geen onderwerpen gevonden.</p> : null}
            <ul className="chatbot-intent-list">
              {filtered.map((r) => (
                <li key={r.key}>
                  <button
                    className={
                      r.key === selectedKey && !creating
                        ? 'chatbot-intent chatbot-intent-active'
                        : 'chatbot-intent'
                    }
                    onClick={() => {
                      setCreating(null);
                      setSelectedKey(r.key);
                    }}
                  >
                    <span className="chatbot-intent-title">{(r.draft ?? r.live).title_nl || r.key}</span>
                    <span className="chatbot-intent-key muted small">{r.key}</span>
                    <span className="chatbot-intent-badges">
                      <IntentBadges row={r} />
                    </span>
                    <span className="chatbot-intent-stats muted small">{statsLine(r)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="chatbot-editor">
            {check ? <FlipsPanel result={check} onClose={() => setCheck(null)} /> : null}
            {creating ? (
              <CreatePanel
                seed={creating}
                existingKeys={intents.map((r) => r.key)}
                placeholders={placeholders}
                onCreated={(row) => {
                  replaceIntent(row);
                  setCreating(null);
                  setSelectedKey(row.key);
                }}
                onCancel={() => setCreating(null)}
                onError={setError}
              />
            ) : selected ? (
              <IntentEditor
                key={selected.key}
                intent={selected}
                placeholders={placeholders}
                onChanged={replaceIntent}
                onCheck={runCheck}
                onError={setError}
              />
            ) : (
              <section className="marketing-panel">
                <p className="muted">Kies links een onderwerp, of maak een nieuw intent.</p>
              </section>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function statsLine(r: IntentRow): string {
  const s = r.stats;
  if (!s) return 'nog geen cijfers';
  return `${s.fired_7d}x in 7 dagen · ${s.helpful} nuttig · ${s.unhelpful} niet nuttig · ${s.handoff_7d} naar collega`;
}

function IntentBadges({ row }: { row: IntentRow }) {
  return (
    <>
      {row.live.auto ? (
        <span className="pill chatbot-pill-auto">Automatisch</span>
      ) : (
        <span className="pill chatbot-pill-handoff">Naar collega</span>
      )}
      {row.draft ? <span className="pill chatbot-pill-draft">Concept</span> : null}
      {!row.enabled ? <span className="pill chatbot-pill-off">Uit</span> : null}
      {row.kind === 'code' ? <span className="pill chatbot-pill-code">Met logica</span> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Flips: voorbeeldzinnen die na een wijziging ergens anders landen
// ---------------------------------------------------------------------------
function FlipsPanel({ result, onClose }: { result: ExampleCheckResult; onClose: () => void }) {
  const flipped = result.results.filter((r) => !r.ok);
  return (
    <section className={result.flips > 0 ? 'marketing-panel chatbot-flips chatbot-flips-warn' : 'marketing-panel chatbot-flips'}>
      <div className="chatbot-flips-head">
        <h2>
          {result.flips === 0
            ? 'Alle voorbeeldzinnen landen nog op hun eigen onderwerp.'
            : result.flips === 1
              ? '1 voorbeeldzin landt nu ergens anders'
              : `${result.flips} voorbeeldzinnen landen nu ergens anders`}
        </h2>
        <button className="btn-link" onClick={onClose}>Sluiten</button>
      </div>
      {flipped.length > 0 ? (
        <table className="vouchers-table chatbot-table">
          <thead>
            <tr>
              <th>Hoort bij</th>
              <th>Voorbeeldzin</th>
              <th>Landt nu op</th>
              <th>Zekerheid</th>
            </tr>
          </thead>
          <tbody>
            {flipped.map((r, i) => (
              <tr key={`${r.key}-${i}`}>
                <td><code>{r.key}</code></td>
                <td>{r.example}</td>
                <td><code>{r.picked}</code></td>
                <td>{pct(r.confidence)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Editor voor een bestaand onderwerp
// ---------------------------------------------------------------------------
function IntentEditor({
  intent,
  placeholders,
  onChanged,
  onCheck,
  onError,
}: {
  intent: IntentRow;
  placeholders: string[];
  onChanged: (row: IntentRow) => void;
  onCheck: (useDrafts: boolean) => Promise<void>;
  onError: (e: string | null) => void;
}) {
  const [fields, setFields] = useState<IntentFields>(() => intent.draft ?? intent.live);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The row can change under the editor (our own save echo, a discard, an
  // approve, an example added from the unanswered tab). Follow it instead of
  // remounting, so the notice and the preview toggles survive a save.
  useEffect(() => {
    setFields(intent.draft ?? intent.live);
  }, [intent.draft, intent.live]);
  const dirty = !fieldsEqual(fields, intent.draft ?? intent.live);

  const save = async (): Promise<IntentRow | null> => {
    const problem = fieldsProblem(fields, intent.kind);
    if (problem) {
      onError(problem);
      return null;
    }
    const res = await saveIntentDraft(intent.key, cleanFields(fields), intent.enabled);
    if (!res.ok || !res.data) {
      onError(errText(res.error));
      return null;
    }
    onChanged(res.data.intent);
    return res.data.intent;
  };

  const handleSave = async () => {
    setBusy('save');
    onError(null);
    setNotice(null);
    const saved = await save();
    if (saved) {
      setNotice('Concept opgeslagen. De bot gebruikt nog de live versie.');
      await onCheck(true);
    }
    setBusy(null);
  };

  const handleDiscard = async () => {
    if (!window.confirm('Concept weggooien? De live versie blijft zoals hij is.')) return;
    setBusy('discard');
    onError(null);
    const res = await discardIntentDraft(intent.key);
    setBusy(null);
    if (res.ok && res.data) onChanged(res.data.intent);
    else onError(errText(res.error));
  };

  const handleApprove = async () => {
    const msg = dirty
      ? 'Je hebt nog niet-opgeslagen wijzigingen. Die worden eerst opgeslagen en dan live gezet. Doorgaan?'
      : 'Concept goedkeuren en live zetten? De bot gebruikt dit antwoord meteen.';
    if (!window.confirm(msg)) return;
    setBusy('approve');
    onError(null);
    setNotice(null);
    if (dirty) {
      const saved = await save();
      if (!saved) {
        setBusy(null);
        return;
      }
    }
    const res = await approveIntent(intent.key);
    if (res.ok && res.data) {
      onChanged(res.data.intent);
      setNotice(`Live gezet als versie ${res.data.intent.version}.`);
      await onCheck(false);
    } else {
      onError(errText(res.error));
    }
    setBusy(null);
  };

  // Enabled rides along on save_draft. The draft sent is what is already
  // saved (or live when there is none), so a plain on/off switch does not
  // quietly turn unsaved edits into a draft.
  const handleToggleEnabled = async () => {
    const turningOff = intent.enabled;
    if (turningOff && !window.confirm('Onderwerp uitschakelen? De bot negeert het dan helemaal.')) return;
    setBusy('enabled');
    onError(null);
    const res = await saveIntentDraft(intent.key, intent.draft ?? intent.live, !intent.enabled);
    setBusy(null);
    if (res.ok && res.data) {
      onChanged(res.data.intent);
      setNotice(turningOff ? 'Onderwerp uitgeschakeld.' : 'Onderwerp ingeschakeld.');
    } else {
      onError(errText(res.error));
    }
  };

  return (
    <>
      <section className="marketing-panel">
        <div className="chatbot-editor-head">
          <div>
            <h2>
              {(intent.draft ?? intent.live).title_nl || intent.key}{' '}
              <code className="muted small">{intent.key}</code>
            </h2>
            <div className="chatbot-intent-badges"><IntentBadges row={intent} /></div>
          </div>
          <div className="muted small chatbot-editor-meta">
            Versie {intent.version}
            {intent.approved_at ? ` · live sinds ${fmtDateTime(intent.approved_at)}` : ''}
            {intent.approved_by_name ? ` door ${intent.approved_by_name}` : ''}
            {intent.updated_at ? ` · gewijzigd ${fmtDateTime(intent.updated_at)}` : ''}
            {intent.source ? <div>Bron: {intent.source}</div> : null}
          </div>
        </div>
        {intent.kind === 'code' ? (
          <p className="muted marketing-hint">
            Dit onderwerp heeft logica in de code: de antwoordtekst zie je hieronder in het
            voorbeeld. Hier pas je alleen de criteria, de titels en de schakelaars aan.
          </p>
        ) : null}
        {intent.draft ? (
          <p className="muted marketing-hint">
            Er staat een concept klaar. De bot gebruikt nog versie {intent.version}; zet het concept
            live met "Goedkeuren en live zetten".
          </p>
        ) : null}

        <IntentFieldsForm
          fields={fields}
          onChange={setFields}
          kind={intent.kind}
          lockedAuto={intent.locked_auto}
          placeholders={placeholders}
        />

        {notice ? <p className="marketing-notice">{notice}</p> : null}

        <div className="marketing-actions">
          <button className="btn-secondary" disabled={busy !== null || !dirty} onClick={() => void handleSave()}>
            {busy === 'save' ? 'Opslaan…' : 'Opslaan als concept'}
          </button>
          {intent.draft ? (
            <button className="btn-link voucher-deactivate" disabled={busy !== null} onClick={() => void handleDiscard()}>
              {busy === 'discard' ? 'Weggooien…' : 'Concept weggooien'}
            </button>
          ) : null}
          <button
            className="walkin-primary-btn"
            disabled={busy !== null || (!intent.draft && !dirty)}
            onClick={() => void handleApprove()}
          >
            {busy === 'approve' ? 'Live zetten…' : 'Goedkeuren en live zetten'}
          </button>
          <button className="btn-link" disabled={busy !== null} onClick={() => void handleToggleEnabled()}>
            {busy === 'enabled' ? 'Bezig…' : intent.enabled ? 'Uitschakelen' : 'Inschakelen'}
          </button>
        </div>
      </section>

      <PreviewPanel
        intentKey={intent.key}
        fields={fields}
        // The live preview only shows the draft wording when there is one to
        // show: a saved draft or edits in progress.
        sendDraft={intent.draft !== null || dirty}
      />
      <ClassifyBox />
    </>
  );
}

// ---------------------------------------------------------------------------
// Nieuw onderwerp
// ---------------------------------------------------------------------------
function CreatePanel({
  seed,
  existingKeys,
  placeholders,
  onCreated,
  onCancel,
  onError,
}: {
  seed: IntentFields;
  existingKeys: string[];
  placeholders: string[];
  onCreated: (row: IntentRow) => void;
  onCancel: () => void;
  onError: (e: string | null) => void;
}) {
  const [key, setKey] = useState('');
  const [fields, setFields] = useState<IntentFields>(seed);
  const [busy, setBusy] = useState(false);

  const keyProblem = !key
    ? null
    : !INTENT_KEY_RE.test(key)
      ? ERROR_LABEL.invalid_key ?? null
      : existingKeys.includes(key)
        ? ERROR_LABEL.key_exists ?? null
        : null;

  const handleCreate = async () => {
    onError(null);
    if (!key || keyProblem) {
      onError(keyProblem ?? 'Vul een sleutel in.');
      return;
    }
    const problem = fieldsProblem(fields, 'plain');
    if (problem) {
      onError(problem);
      return;
    }
    setBusy(true);
    const res = await createIntent(key, cleanFields(fields));
    setBusy(false);
    if (res.ok && res.data) onCreated(res.data.intent);
    else onError(errText(res.error));
  };

  return (
    <section className="marketing-panel">
      <h2>Nieuw intent</h2>
      <p className="muted marketing-hint">
        Een nieuw onderwerp begint als concept. Na goedkeuren draait het eerst een week in de
        schaduw (altijd "Naar collega", ook als je "Automatisch" aanvinkt); daarna kun je
        "Automatisch" aanzetten.
      </p>
      <label className="walkin-field">
        <span>Sleutel (kleine letters, cijfers en _; kan later niet meer veranderen)</span>
        <input
          type="text"
          value={key}
          placeholder="bijv. parkeren"
          onChange={(e) => setKey(e.target.value.trim().toLowerCase())}
          spellCheck={false}
        />
        {keyProblem ? <span className="chatbot-field-error">{keyProblem}</span> : null}
      </label>
      <IntentFieldsForm
        fields={fields}
        onChange={setFields}
        kind="plain"
        lockedAuto={false}
        placeholders={placeholders}
      />
      <div className="marketing-actions">
        <button className="btn-link" disabled={busy} onClick={onCancel}>Annuleren</button>
        <button className="walkin-primary-btn" disabled={busy} onClick={() => void handleCreate()}>
          {busy ? 'Aanmaken…' : 'Aanmaken als concept'}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// De velden van een onderwerp (gedeeld door editor en nieuw-formulier)
// ---------------------------------------------------------------------------
function IntentFieldsForm({
  fields,
  onChange,
  kind,
  lockedAuto,
  placeholders,
}: {
  fields: IntentFields;
  onChange: (f: IntentFields) => void;
  kind: 'plain' | 'code';
  lockedAuto: boolean;
  placeholders: string[];
}) {
  const set = <K extends keyof IntentFields>(k: K, v: IntentFields[K]) => onChange({ ...fields, [k]: v });
  const codeKind = kind === 'code';

  const setLink = (i: number, patch: Partial<IntentFields['links'][number]>) => {
    const links = fields.links.slice();
    const cur = links[i];
    if (!cur) return;
    links[i] = { ...cur, ...patch };
    set('links', links);
  };

  const toggleAction = (a: IntentActionKind) =>
    set(
      'actions',
      fields.actions.includes(a) ? fields.actions.filter((x) => x !== a) : [...fields.actions, a],
    );

  const exampleCount = fields.examples.filter((s) => s.trim().length > 0).length;

  return (
    <div className="chatbot-fields">
      <label className="walkin-field">
        <span>Criteria: waar gaat dit onderwerp over?</span>
        <textarea
          rows={3}
          value={fields.criteria}
          onChange={(e) => set('criteria', e.target.value)}
          placeholder="Parking near the venue [parkeren, parkeergarage, auto kwijt]"
        />
        <span className="muted small">
          Eén regel: een Engelse omschrijving plus Nederlandse trefwoorden tussen haken. Let op: elk
          trefwoord dat je toevoegt of weghaalt verschuift ook de buren. Na het opslaan zie je welke
          voorbeeldzinnen daardoor ergens anders landen.
        </span>
      </label>

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Titel (NL)</span>
          <input type="text" value={fields.title_nl} onChange={(e) => set('title_nl', e.target.value)} />
        </label>
        <label className="walkin-field">
          <span>Titel (EN)</span>
          <input type="text" value={fields.title_en} onChange={(e) => set('title_en', e.target.value)} />
        </label>
      </div>

      {codeKind ? (
        <p className="muted small">
          De antwoordtekst, links en knoppen van dit onderwerp komen uit de code en staan in het
          voorbeeld hieronder.
        </p>
      ) : (
        <>
          <label className="walkin-field">
            <span>Antwoord (NL)</span>
            <textarea
              rows={6}
              value={fields.text_nl ?? ''}
              onChange={(e) => set('text_nl', e.target.value)}
            />
          </label>
          <label className="walkin-field">
            <span>Antwoord (EN)</span>
            <textarea
              rows={6}
              value={fields.text_en ?? ''}
              onChange={(e) => set('text_en', e.target.value)}
            />
          </label>
          {placeholders.length > 0 ? (
            <p className="muted small chatbot-placeholders">
              Toegestane plaatshouders in de tekst:{' '}
              {placeholders.map((p) => <code key={p}>{p}</code>)}
            </p>
          ) : null}

          <div className="chatbot-links">
            <span className="marketing-block-type">Links</span>
            {fields.links.map((l, i) => (
              <div key={i} className="walkin-row chatbot-link-row">
                <input
                  type="text"
                  value={l.label_nl}
                  placeholder="Linktekst (NL)"
                  aria-label={`Link ${i + 1} tekst NL`}
                  onChange={(e) => setLink(i, { label_nl: e.target.value })}
                />
                <input
                  type="text"
                  value={l.label_en}
                  placeholder="Link text (EN)"
                  aria-label={`Link ${i + 1} tekst EN`}
                  onChange={(e) => setLink(i, { label_en: e.target.value })}
                />
                <input
                  type="url"
                  value={l.url}
                  placeholder="https://…"
                  aria-label={`Link ${i + 1} url`}
                  onChange={(e) => setLink(i, { url: e.target.value })}
                />
                <button
                  className="btn-link voucher-deactivate"
                  onClick={() => set('links', fields.links.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-secondary"
              onClick={() => set('links', [...fields.links, { label_nl: '', label_en: '', url: '' }])}
            >
              + Link
            </button>
          </div>

          <div className="chatbot-checks">
            <span className="marketing-block-type">Knoppen onder het antwoord</span>
            {ACTION_KINDS.map((a) => (
              <label key={a} className="chatbot-check">
                <input type="checkbox" checked={fields.actions.includes(a)} onChange={() => toggleAction(a)} />
                <span>{ACTION_LABEL[a]}</span>
              </label>
            ))}
          </div>
        </>
      )}

      <div className="chatbot-checks">
        <label className="chatbot-check">
          <input
            type="checkbox"
            checked={lockedAuto ? false : fields.auto}
            disabled={lockedAuto}
            onChange={(e) => set('auto', e.target.checked)}
          />
          <span>Automatisch beantwoorden (zonder collega)</span>
        </label>
        {lockedAuto ? (
          <span className="muted small">
            Dit onderwerp mag de bot nooit alleen afhandelen (klachten, annuleringen, geld,
            groepsgrootte, zakelijk, pers). Er gaat altijd een collega mee.
          </span>
        ) : null}
        <label className="chatbot-check">
          <input
            type="checkbox"
            checked={fields.venue_dependent}
            onChange={(e) => set('venue_dependent', e.target.checked)}
          />
          <span>Antwoord verschilt per vestiging</span>
        </label>
      </div>

      <label className="walkin-field">
        <span>Voorbeeldzinnen (één per regel, {exampleCount} van 5 tot 10)</span>
        <textarea
          rows={8}
          value={fields.examples.join('\n')}
          onChange={(e) => set('examples', e.target.value.split('\n'))}
          placeholder={'Waar kan ik parkeren?\nIs er een parkeergarage in de buurt?'}
        />
        <span className="muted small">
          Schrijf ze zelf, zoals een klant het zou typen. Gebruik nooit echte klantteksten.
        </span>
      </label>

      <label className="walkin-field">
        <span>Notities (alleen voor ons)</span>
        <textarea
          rows={2}
          value={fields.notes ?? ''}
          onChange={(e) => set('notes', e.target.value || null)}
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voorbeeld: door de echte code gerenderd, per taal en vestiging
// ---------------------------------------------------------------------------
function PreviewPanel({
  intentKey,
  fields,
  sendDraft,
}: {
  intentKey: string;
  fields: IntentFields;
  sendDraft: boolean;
}) {
  const [locale, setLocale] = useState<IntentLocale>('nl');
  const [venue, setVenue] = useState<PreviewVenue>('eindhoven');
  const [preview, setPreview] = useState<IntentPreview | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const seq = useRef(0);

  useEffect(() => {
    setState('loading');
    const mySeq = ++seq.current;
    const t = setTimeout(async () => {
      const res = await previewIntent({
        key: intentKey,
        ...(sendDraft ? { draft: cleanFields(fields) } : {}),
        locale,
        venue,
      });
      if (seq.current !== mySeq) return; // stale answer
      if (res.ok && res.data) {
        setPreview(res.data);
        setState('idle');
      } else {
        setState('error');
      }
    }, 400);
    return () => clearTimeout(t);
  }, [intentKey, fields, sendDraft, locale, venue]);

  return (
    <section className="marketing-panel chatbot-preview">
      <div className="marketing-preview-head chatbot-preview-head">
        <span className="marketing-block-type">
          Voorbeeld {sendDraft ? '(concept)' : '(live)'}
        </span>
        <span className="marketing-block-tools">
          {state === 'loading' ? <span className="muted small">renderen…</span> : null}
          {state === 'error' ? <span className="muted small">voorbeeld niet beschikbaar</span> : null}
          {(['nl', 'en'] as IntentLocale[]).map((l) => (
            <button
              key={l}
              className={locale === l ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setLocale(l)}
            >
              {l.toUpperCase()}
            </button>
          ))}
          {VENUES.map((v) => (
            <button
              key={v}
              className={venue === v ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setVenue(v)}
            >
              {VENUE_LABEL[v]}
            </button>
          ))}
        </span>
      </div>
      {preview ? (
        <div className="chatbot-bubble">
          <div className="chatbot-bubble-title">{preview.title}</div>
          <div className="chatbot-bubble-text">{preview.text}</div>
          {preview.links.length > 0 ? (
            <ul className="chatbot-bubble-links">
              {preview.links.map((l, i) => (
                <li key={i}>
                  <a href={l.url} target="_blank" rel="noreferrer">{l.label}</a>
                </li>
              ))}
            </ul>
          ) : null}
          {preview.actions.length > 0 ? (
            <div className="chatbot-bubble-actions">
              {preview.actions.map((a, i) => (
                <span key={i} className="pill">{a.label}</span>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <p className="muted small">Nog geen voorbeeld.</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Test een zin: wat kiest Jev?
// ---------------------------------------------------------------------------
function ClassifyBox() {
  const [text, setText] = useState('');
  const [useDrafts, setUseDrafts] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ClassifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    const res = await classifyText(text.trim(), useDrafts);
    setBusy(false);
    if (res.ok && res.data) setResult(res.data);
    else setError(errText(res.error));
  };

  return (
    <section className="marketing-panel">
      <h2>Test een zin</h2>
      <p className="muted marketing-hint">
        Typ een zin zoals een klant die zou stellen en zie welk onderwerp de bot herkent. Er wordt
        niets bewaard.
      </p>
      <form
        className="chatbot-classify-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <input
          type="text"
          value={text}
          aria-label="Testzin"
          placeholder="Kan ik mijn boeking verzetten naar volgende week?"
          onChange={(e) => setText(e.target.value)}
        />
        <button className="btn-secondary" type="submit" disabled={busy || !text.trim()}>
          {busy ? 'Bezig…' : 'Test'}
        </button>
      </form>
      <label className="chatbot-check">
        <input type="checkbox" checked={useDrafts} onChange={(e) => setUseDrafts(e.target.checked)} />
        <span>Met concepten (anders alleen wat live is)</span>
      </label>
      {error ? <div className="error-card">{error}</div> : null}
      {result ? (
        <div className="chatbot-classify-result">
          <p>
            Jev kiest <strong><code>{result.intent}</code></strong> ({pct(result.confidence)} zeker).
            {' '}Taal: {result.language}.
            {' '}Wil een mens: {pct(result.wants_person)}.
            {' '}Meerdere vragen: {pct(result.multiple_questions)}.
          </p>
          {result.top.length > 0 ? (
            <ul className="muted small chatbot-classify-top">
              {result.top.map((t) => (
                <li key={t.key}>
                  <code>{t.key}</code> {t.title} · {pct(t.probability)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Onbeantwoord: wat de bot niet kon plaatsen of doorgaf
// ---------------------------------------------------------------------------
function UnansweredTab({
  intents,
  onNewIntent,
  onAssigned,
  onError,
}: {
  intents: IntentRow[];
  onNewIntent: (text: string) => void;
  onAssigned: (row: IntentRow) => void;
  onError: (e: string | null) => void;
}) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<UnansweredResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Which row has its "Hoort bij…" picker open, and the chosen key.
  const [picking, setPicking] = useState<string | null>(null);
  const [pickKey, setPickKey] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listUnanswered(days).then((res) => {
      if (cancelled) return;
      if (res.ok && res.data) setData(res.data);
      else onError(errText(res.error));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [days, onError]);

  // Appends the sentence to the target's draft examples (on top of the saved
  // draft, or live when there is none). Enabled is left alone.
  const assign = async (rowId: string, text: string) => {
    const target = intents.find((r) => r.key === pickKey);
    if (!target) return;
    setBusy(rowId);
    onError(null);
    const base = target.draft ?? target.live;
    const examples = base.examples.includes(text) ? base.examples : [...base.examples, text];
    const res = await saveIntentDraft(target.key, { ...base, examples });
    setBusy(null);
    if (res.ok && res.data) {
      onAssigned(res.data.intent);
      setDone((d) => ({ ...d, [rowId]: target.key }));
      setPicking(null);
    } else {
      onError(errText(res.error));
    }
  };

  // "Niet voor de bot": the row disappears from both lists at once.
  const dismiss = async (rowId: string) => {
    setBusy(rowId);
    const res = await dismissUnanswered(rowId);
    setBusy(null);
    if (!res.ok) {
      onError(errText(res.error));
      return;
    }
    setData((d) =>
      d ? { ...d, none: d.none.filter((r) => r.id !== rowId), handoffs: d.handoffs.filter((r) => r.id !== rowId) } : d,
    );
  };

  const rowTools = (rowId: string, text: string) => {
    const assigned = done[rowId];
    if (assigned) {
      return <span className="muted small">Toegevoegd aan <code>{assigned}</code></span>;
    }
    if (picking === rowId) {
      return (
        <span className="chatbot-pick">
          <select
            aria-label="Kies onderwerp"
            value={pickKey}
            onChange={(e) => setPickKey(e.target.value)}
          >
            <option value="">Kies onderwerp…</option>
            {intents.map((r) => (
              <option key={r.key} value={r.key}>
                {(r.draft ?? r.live).title_nl || r.key} ({r.key})
              </option>
            ))}
          </select>
          <button
            className="btn-secondary"
            disabled={!pickKey || busy === rowId}
            onClick={() => void assign(rowId, text)}
          >
            {busy === rowId ? 'Bezig…' : 'Toevoegen'}
          </button>
          <button className="btn-link" onClick={() => setPicking(null)}>Annuleren</button>
        </span>
      );
    }
    return (
      <span className="chatbot-row-tools">
        <button className="btn-link" onClick={() => onNewIntent(text)}>Nieuw intent hiervan</button>
        <button
          className="btn-link"
          onClick={() => {
            setPickKey('');
            setPicking(rowId);
          }}
        >
          Hoort bij…
        </button>
        <button
          className="btn-link"
          disabled={busy === rowId}
          title="Een partner, leverancier of sollicitant: dit komt nooit via de website of de chat"
          onClick={() => void dismiss(rowId)}
        >
          Niet voor de bot
        </button>
      </span>
    );
  };

  return (
    <div className="chatbot-unanswered">
      <section className="marketing-panel">
        <div className="chatbot-editor-head">
          <h2>Onbeantwoord</h2>
          <label className="walkin-field chatbot-days">
            <span>Periode</span>
            <select value={days} aria-label="Periode" onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>7 dagen</option>
              <option value={14}>14 dagen</option>
              <option value={30}>30 dagen</option>
            </select>
          </label>
        </div>
        <p className="muted marketing-hint">
          Klantzinnen die de bot nergens kon plaatsen of doorgaf aan een collega, met
          persoonsgegevens weggehaald. Maak er een nieuw onderwerp van, of voeg de zin als
          voorbeeld toe aan een bestaand onderwerp.
        </p>
        {loading ? <p className="muted">Laden…</p> : null}
      </section>

      {data ? (
        <>
          <section className="marketing-panel">
            <h2>Niet geplaatst ({data.none.length})</h2>
            {data.none.length === 0 ? <p className="muted">Niets in deze periode.</p> : null}
            {data.none.length > 0 ? (
              <table className="vouchers-table chatbot-table">
                <thead>
                  <tr>
                    <th>Wanneer</th>
                    <th>Kanaal</th>
                    <th>Zin</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.none.map((r) => (
                    <tr key={r.id}>
                      <td className="muted">{fmtDateTime(r.created_at)}</td>
                      <td className="muted">{r.channel}</td>
                      <td>{r.text}</td>
                      <td>{rowTools(r.id, r.text)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </section>

          <section className="marketing-panel">
            <h2>Doorgegeven aan een collega ({data.handoffs.length})</h2>
            {data.handoffs.length === 0 ? <p className="muted">Niets in deze periode.</p> : null}
            {data.handoffs.length > 0 ? (
              <table className="vouchers-table chatbot-table">
                <thead>
                  <tr>
                    <th>Wanneer</th>
                    <th>Onderwerp</th>
                    <th>Zin</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.handoffs.map((r) => (
                    <tr key={r.id}>
                      <td className="muted">{fmtDateTime(r.created_at)}</td>
                      <td><code>{r.intent}</code></td>
                      <td>{r.text}</td>
                      <td>{rowTools(r.id, r.text)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </section>

          {data.counts.length > 0 ? (
            <section className="marketing-panel">
              <h2>Per onderwerp</h2>
              <table className="vouchers-table chatbot-table">
                <thead>
                  <tr>
                    <th>Onderwerp</th>
                    <th>Herkend</th>
                    <th>Naar collega</th>
                  </tr>
                </thead>
                <tbody>
                  {data.counts.map((c) => (
                    <tr key={c.intent}>
                      <td><code>{c.intent}</code></td>
                      <td>{c.fired}</td>
                      <td>{c.handoff}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
